import { useLingui } from "@lingui/react/macro";
import {
  browserSupportsWebAuthn,
  WebAuthnAbortService,
  WebAuthnError,
} from "@simplewebauthn/browser";
import { type RefObject, useEffect, useRef, useState } from "react";

import { unsupportedByBrowserMsg } from "../../../app/common-messages";

/**
 * The browser's passkey prompt a screen is waiting on, so the screen can stop
 * waiting. A password manager may never answer a prompt it dropped, so a
 * cancelled attempt isn't waited out: whatever it reports later is ignored.
 *
 * A prompt still open when the screen goes away is called off too.
 */
export function usePasskeyPrompt() {
  // Bumped by every attempt and every cancel; an attempt is current while it
  // still holds the latest number.
  const latest = useRef(0);

  useEffect(
    () => () => {
      callOff(latest);
    },
    [],
  );

  return {
    /** @returns whether this attempt is still the one being waited on. */
    start() {
      const attempt = ++latest.current;
      return () => attempt === latest.current;
    },
    cancel() {
      callOff(latest);
    },
  };
}

/**
 * Whether this browser can use passkeys, and if not, the reason to show on
 * whatever it disables. Decided once: a browser doesn't gain or lose WebAuthn
 * while a screen is up.
 */
export function usePasskeySupport() {
  const { t } = useLingui();
  const [supported] = useState(browserSupportsWebAuthn);

  return {
    supported,
    unsupportedReason: supported ? undefined : t(unsupportedByBrowserMsg),
  };
}

/** The passkey prompt was closed, or left until it timed out. */
export function isDismissedPrompt(error: unknown) {
  return error instanceof WebAuthnError && error.name === "NotAllowedError";
}

/** The authenticator already holds a passkey for this account. */
export function isAlreadyRegistered(error: unknown) {
  return (
    error instanceof WebAuthnError &&
    error.code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED"
  );
}

function callOff(latest: RefObject<number>) {
  latest.current += 1;
  // Closes the browser's own prompt, if one is open. A password manager may
  // ignore it.
  WebAuthnAbortService.cancelCeremony();
}
